import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaV1Service, serviceName } from '../service';
import { GetSchedulingStaffsIO, ServiceQueries } from '../types';
import { useV1Queries } from './use-v1-queries';
import { UseV1QueryEndpointArgs, useV1Query } from './use-v1-query';

/**
 * A hook that returns a query for the `GetSchedulingStaffs` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSchedulingStaffsQuery = <E = unknown, D = GetSchedulingStaffsIO['output']>(
  args: UseV1QueryEndpointArgs<'GetSchedulingStaffs', E, D>
) =>
  useV1Query({
    endpointName: 'GetSchedulingStaffs',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetSchedulingStaffs` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSchedulingStaffsQueries = <E = unknown, D = GetSchedulingStaffsIO['output']>(
  argsArr: UseV1QueryEndpointArgs<'GetSchedulingStaffs', E, D>[]
) =>
  useV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetSchedulingStaffs',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetSchedulingStaffs` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetSchedulingStaffs = () => {
  const queryClient = useQueryClient();

  const fetchGetSchedulingStaffs = async <E = unknown, D = GetSchedulingStaffsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseV1QueryEndpointArgs<'GetSchedulingStaffs', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetSchedulingStaffsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetSchedulingStaffs'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetSchedulingStaffsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetSchedulingStaffs'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetSchedulingStaffs'>({
          serviceName,
          endpointName: 'GetSchedulingStaffs',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaV1Service.GetSchedulingStaffs(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetSchedulingStaffs;
};
